import { useEffect, useMemo, useState } from 'react'
import { supabase } from '../lib/supabase'
import Modal from '../components/ui/Modal'
import { exportStrategicPlanDocx } from '../lib/exportPlan'
import { PLANS } from '../constants/pricing'
import AdminMetricsView from '../components/admin/AdminMetricsView'
import {
  Users, Plus, Pencil, ShieldCheck, User, Eye, Trash2, FileText,
  CheckCircle2, Clock, AlertTriangle, XCircle, BarChart3,
} from 'lucide-react'

const ROLE_LABELS = { admin: 'Administrador', consultant: 'Consultor NVC', client: 'Cliente', caja_user: 'Caja', client_ceo: 'CEO Cliente' }
const ROLE_COLORS = {
  admin:      'bg-red-100 text-red-700',
  consultant: 'bg-brand-100 text-brand-700',
  client:     'bg-green-100 text-green-700',
  caja_user:  'bg-amber-100 text-amber-700',
  client_ceo: 'bg-purple-100 text-purple-700',
}

const SUB_BADGE = {
  active:    { label: 'Activa',      icon: CheckCircle2, className: 'bg-emerald-100 text-emerald-700' },
  trial:     { label: 'En prueba',   icon: Clock,        className: 'bg-amber-100 text-amber-700' },
  expired:   { label: 'Expirada',    icon: AlertTriangle, className: 'bg-red-100 text-red-700' },
  cancelled: { label: 'Cancelada',   icon: XCircle,      className: 'bg-gray-100 text-gray-600' },
}

export default function AdminPage() {
  const [tab, setTab]                       = useState('nvc') // 'nvc' | 'caja'
  const [users, setUsers]                   = useState([])
  const [consultancies, setConsultancies]   = useState([])
  const [cajaProjects, setCajaProjects]     = useState([])  // user_id → project + progress
  const [loading, setLoading]               = useState(true)
  const [showModal, setShowModal]           = useState(false)
  const [editUser, setEditUser]             = useState(null)
  const [saving, setSaving]                 = useState(false)
  const [exportingFor, setExportingFor]     = useState(null)
  const [form, setForm] = useState({ email: '', full_name: '', role: 'consultant', password: '', consultancy_id: '' })
  const [error, setError] = useState('')

  useEffect(() => { fetchData() }, [])

  async function fetchData() {
    setLoading(true)
    const [{ data: u }, { data: c }] = await Promise.all([
      supabase.from('profiles').select('*').order('role').order('full_name'),
      supabase.from('consultancies').select('id, name, client_company, is_caja').order('name'),
    ])
    setUsers(u ?? [])
    setConsultancies(c ?? [])

    // Hydrate Caja project info: link each caja_user to their consultancy + progress
    const cajaUserIds = (u ?? []).filter(x => x.role === 'caja_user').map(x => x.id)
    if (cajaUserIds.length > 0) {
      const { data: links } = await supabase
        .from('consultancy_users')
        .select('user_id, consultancy_id')
        .in('user_id', cajaUserIds)

      const consultancyIds = [...new Set((links ?? []).map(l => l.consultancy_id))]
      const [{ data: cajaCons }, { data: phases }] = await Promise.all([
        supabase.from('consultancies').select('id, client_company, industry').in('id', consultancyIds),
        supabase.from('phases')
          .select('id, consultancy_id, sections(id, status)')
          .in('consultancy_id', consultancyIds)
          .is('business_unit_id', null),
      ])

      const consById = new Map((cajaCons ?? []).map(x => [x.id, x]))
      const progressById = new Map()
      for (const ph of phases ?? []) {
        const total = ph.sections?.length ?? 0
        const done  = ph.sections?.filter(s => s.status === 'completed').length ?? 0
        const prev  = progressById.get(ph.consultancy_id) ?? { total: 0, done: 0 }
        progressById.set(ph.consultancy_id, { total: prev.total + total, done: prev.done + done })
      }

      const projects = (links ?? []).map(l => {
        const cons = consById.get(l.consultancy_id)
        const prog = progressById.get(l.consultancy_id) ?? { total: 0, done: 0 }
        return {
          user_id:        l.user_id,
          consultancy_id: l.consultancy_id,
          client_company: cons?.client_company ?? '—',
          industry:       cons?.industry ?? '',
          progress_pct:   prog.total > 0 ? Math.round((prog.done / prog.total) * 100) : 0,
        }
      })
      setCajaProjects(projects)
    } else {
      setCajaProjects([])
    }

    setLoading(false)
  }

  function openCreate() {
    setEditUser(null)
    setForm({ email: '', full_name: '', role: 'consultant', password: '', consultancy_id: '' })
    setError('')
    setShowModal(true)
  }

  function openEdit(user) {
    setEditUser(user)
    setForm({ email: user.email ?? '', full_name: user.full_name, role: user.role, password: '', consultancy_id: '' })
    setError('')
    setShowModal(true)
  }

  async function saveUser(e) {
    e.preventDefault()
    setSaving(true)
    setError('')
    try {
      if (editUser) {
        const { error: err } = await supabase
          .from('profiles')
          .update({ full_name: form.full_name, role: form.role })
          .eq('id', editUser.id)
        if (err) throw err

        if (form.role === 'client' && form.consultancy_id) {
          await supabase.from('consultancy_users').upsert({
            consultancy_id: form.consultancy_id,
            user_id: editUser.id,
            role: 'client',
          })
        }
      } else {
        const { error: fnError } = await supabase.functions.invoke('create-user', {
          body: {
            email: form.email,
            password: form.password,
            full_name: form.full_name,
            role: form.role,
            consultancy_id: ['client', 'client_ceo'].includes(form.role) ? form.consultancy_id : null,
          }
        })
        if (fnError) {
          try {
            const errBody = await fnError.context?.json()
            throw new Error(errBody?.error ?? fnError.message)
          } catch (parseErr) {
            if (parseErr.message && parseErr.message !== fnError.message) throw parseErr
            throw fnError
          }
        }
      }
      setShowModal(false)
      fetchData()
    } catch (err) {
      setError(err.message ?? 'Error al guardar el usuario')
    } finally {
      setSaving(false)
    }
  }

  async function toggleActive(user) {
    await supabase.from('profiles').update({ active: !user.active }).eq('id', user.id)
    setUsers(u => u.map(x => x.id === user.id ? { ...x, active: !x.active } : x))
  }

  async function deleteUser(user) {
    if (!confirm(`¿Eliminar a ${user.full_name}? Esta acción no se puede deshacer.`)) return
    await supabase.from('consultancy_users').delete().eq('user_id', user.id)
    await supabase.from('profiles').delete().eq('id', user.id)
    setUsers(u => u.filter(x => x.id !== user.id))
  }

  async function handleExportFor(consultancyId, variant = 'full') {
    setExportingFor(`${consultancyId}:${variant}`)
    try {
      await exportStrategicPlanDocx(consultancyId, variant)
    } catch (err) {
      alert(err.message ?? 'No se pudo generar el plan')
    } finally {
      setExportingFor(null)
    }
  }

  const byRole = useMemo(() => ({
    admin:      users.filter(u => u.role === 'admin'),
    consultant: users.filter(u => u.role === 'consultant'),
    client:     users.filter(u => u.role === 'client'),
    caja:       users.filter(u => u.role === 'caja_user'),
  }), [users])

  const cajaUsers = useMemo(() => byRole.caja.map(u => ({
    ...u,
    project: cajaProjects.find(p => p.user_id === u.id),
  })), [byRole.caja, cajaProjects])

  const cajaStats = useMemo(() => ({
    total:   cajaUsers.length,
    active:  cajaUsers.filter(u => u.subscription_status === 'active').length,
    trial:   cajaUsers.filter(u => !u.subscription_status || u.subscription_status === 'trial').length,
    expired: cajaUsers.filter(u => u.subscription_status === 'expired').length,
  }), [cajaUsers])

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="text-xl md:text-2xl font-bold text-gray-900">Administración</h1>
          <p className="text-gray-500 text-sm mt-0.5">Gestiona el acceso al sistema y los clientes Caja</p>
        </div>
        {tab === 'nvc' && (
          <button onClick={openCreate} className="btn-primary flex items-center gap-2 whitespace-nowrap text-sm">
            <Plus size={16} /> Nuevo Usuario
          </button>
        )}
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 border-b border-gray-200 overflow-x-auto">
        {[
          { id: 'nvc',     label: `Usuarios NVC (${byRole.admin.length + byRole.consultant.length + byRole.client.length})`, icon: Users },
          { id: 'caja',    label: `Clientes Caja (${cajaStats.total})`, icon: User },
          { id: 'metrics', label: 'Métricas', icon: BarChart3 },
        ].map(t => {
          const Icon = t.icon
          return (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`flex items-center gap-2 text-sm font-medium px-4 py-2.5 border-b-2 transition-colors whitespace-nowrap ${
                tab === t.id
                  ? 'border-brand-700 text-brand-700'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <Icon size={14} />
              {t.label}
            </button>
          )
        })}
      </div>

      {loading && tab !== 'metrics' ? (
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-10 w-10 border-4 border-brand-500 border-t-transparent" />
        </div>
      ) : tab === 'nvc' ? (
        <NvcUsersView
          users={users.filter(u => u.role !== 'caja_user')}
          byRole={byRole}
          openEdit={openEdit}
          toggleActive={toggleActive}
          deleteUser={deleteUser}
        />
      ) : tab === 'caja' ? (
        <CajaUsersView
          cajaUsers={cajaUsers}
          stats={cajaStats}
          exportingFor={exportingFor}
          onExport={handleExportFor}
          deleteUser={deleteUser}
        />
      ) : (
        <AdminMetricsView />
      )}

      {/* User modal */}
      <Modal open={showModal} onClose={() => setShowModal(false)} title={editUser ? 'Editar Usuario' : 'Nuevo Usuario'}>
        <form onSubmit={saveUser} className="space-y-4">
          {error && <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-3 text-sm">{error}</div>}
          {!editUser && (
            <>
              <div>
                <label className="label">Correo electrónico *</label>
                <input type="email" className="input" required value={form.email}
                  onChange={e => setForm(f => ({...f, email: e.target.value}))} placeholder="correo@ejemplo.com" />
              </div>
              <div>
                <label className="label">Contraseña inicial *</label>
                <input type="password" className="input" required minLength={8} value={form.password}
                  onChange={e => setForm(f => ({...f, password: e.target.value}))} placeholder="Mínimo 8 caracteres" />
              </div>
            </>
          )}
          <div>
            <label className="label">Nombre completo *</label>
            <input className="input" required value={form.full_name}
              onChange={e => setForm(f => ({...f, full_name: e.target.value}))} placeholder="Nombre y apellido" />
          </div>
          <div>
            <label className="label">Rol *</label>
            <select className="input" value={form.role}
              onChange={e => setForm(f => ({...f, role: e.target.value}))}>
              <option value="admin">Administrador — acceso total</option>
              <option value="consultant">Consultor NVC — acceso a todas las consultorías</option>
              <option value="client">Cliente — solo ve su consultoría</option>
              <option value="client_ceo">CEO Cliente — lectura + comentarios de UN proyecto</option>
            </select>
          </div>
          {(form.role === 'client' || form.role === 'client_ceo') && (
            <div>
              <label className="label">Asignar a consultoría *</label>
              <select className="input" required value={form.consultancy_id}
                onChange={e => setForm(f => ({...f, consultancy_id: e.target.value}))}>
                <option value="">Seleccionar consultoría...</option>
                {consultancies.filter(c => !c.is_caja).map(c => (
                  <option key={c.id} value={c.id}>{c.name} — {c.client_company}</option>
                ))}
              </select>
            </div>
          )}
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={() => setShowModal(false)} className="btn-secondary flex-1">Cancelar</button>
            <button type="submit" disabled={saving} className="btn-primary flex-1">{saving ? 'Guardando...' : editUser ? 'Guardar cambios' : 'Crear usuario'}</button>
          </div>
        </form>
      </Modal>
    </div>
  )
}

// ── NVC Users (admin / consultant / client) ───────────────────────────────────
function NvcUsersView({ users, byRole, openEdit, toggleActive, deleteUser }) {
  return (
    <>
      <div className="grid grid-cols-3 gap-3 md:gap-4">
        {[
          { label: 'Admins',     count: byRole.admin.length,      icon: ShieldCheck, color: 'text-red-600',   bg: 'bg-red-50'   },
          { label: 'Consultores',count: byRole.consultant.length, icon: User,        color: 'text-brand-600', bg: 'bg-brand-50' },
          { label: 'Clientes',   count: byRole.client.length,     icon: Eye,         color: 'text-green-600', bg: 'bg-green-50' },
        ].map(({ label, count, icon: Icon, color, bg }) => (
          <div key={label} className="card p-4">
            <div className="flex items-center gap-2 md:gap-3">
              <div className={`p-2 rounded-xl ${bg} flex-shrink-0`}><Icon size={18} className={color} /></div>
              <div>
                <div className="text-xl md:text-2xl font-bold">{count}</div>
                <div className="text-xs text-gray-500">{label}</div>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="card p-0 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th className="text-left px-5 py-3 font-semibold text-gray-600">Nombre</th>
                <th className="text-left px-5 py-3 font-semibold text-gray-600">Rol</th>
                <th className="text-left px-5 py-3 font-semibold text-gray-600">Estado</th>
                <th className="text-left px-5 py-3 font-semibold text-gray-600">Creado</th>
                <th className="px-5 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {users.map(user => (
                <tr key={user.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-brand-700 flex items-center justify-center text-white text-xs font-bold">
                        {user.full_name.charAt(0)}
                      </div>
                      <span className="font-medium text-gray-900">{user.full_name}</span>
                    </div>
                  </td>
                  <td className="px-5 py-3">
                    <span className={`badge ${ROLE_COLORS[user.role]}`}>{ROLE_LABELS[user.role]}</span>
                  </td>
                  <td className="px-5 py-3">
                    <span className={`badge ${user.active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                      {user.active ? 'Activo' : 'Inactivo'}
                    </span>
                  </td>
                  <td className="px-5 py-3 text-gray-500">
                    {new Date(user.created_at).toLocaleDateString('es', { day: 'numeric', month: 'short', year: 'numeric' })}
                  </td>
                  <td className="px-5 py-3">
                    <div className="flex justify-end gap-1">
                      <button onClick={() => openEdit(user)} className="p-1.5 text-gray-400 hover:text-brand-600 hover:bg-brand-50 rounded-lg transition-colors">
                        <Pencil size={14} />
                      </button>
                      <button onClick={() => toggleActive(user)}
                        className={`text-xs px-2.5 py-1 rounded-lg border transition-colors ${user.active ? 'border-red-200 text-red-600 hover:bg-red-50' : 'border-green-200 text-green-600 hover:bg-green-50'}`}>
                        {user.active ? 'Desactivar' : 'Activar'}
                      </button>
                      {user.role !== 'admin' && (
                        <button onClick={() => deleteUser(user)} className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors" title="Eliminar usuario">
                          <Trash2 size={14} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </>
  )
}

// ── Caja Clients ──────────────────────────────────────────────────────────────
function CajaUsersView({ cajaUsers, stats, exportingFor, onExport, deleteUser }) {
  return (
    <>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
        {[
          { label: 'Total',     count: stats.total,   icon: Users,         color: 'text-brand-600', bg: 'bg-brand-50' },
          { label: 'Activas',   count: stats.active,  icon: CheckCircle2,  color: 'text-emerald-600', bg: 'bg-emerald-50' },
          { label: 'En prueba', count: stats.trial,   icon: Clock,         color: 'text-amber-600', bg: 'bg-amber-50' },
          { label: 'Expiradas', count: stats.expired, icon: AlertTriangle, color: 'text-red-600',   bg: 'bg-red-50' },
        ].map(({ label, count, icon: Icon, color, bg }) => (
          <div key={label} className="card p-4">
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-xl ${bg} flex-shrink-0`}><Icon size={18} className={color} /></div>
              <div>
                <div className="text-xl md:text-2xl font-bold">{count}</div>
                <div className="text-xs text-gray-500">{label}</div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {cajaUsers.length === 0 ? (
        <div className="card p-10 text-center text-sm text-gray-400">
          Aún no hay clientes Caja registrados.
        </div>
      ) : (
        <div className="card p-0 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  <th className="text-left px-5 py-3 font-semibold text-gray-600">Cliente</th>
                  <th className="text-left px-5 py-3 font-semibold text-gray-600">Empresa</th>
                  <th className="text-left px-5 py-3 font-semibold text-gray-600">Plan</th>
                  <th className="text-left px-5 py-3 font-semibold text-gray-600">Suscripción</th>
                  <th className="text-left px-5 py-3 font-semibold text-gray-600">Vence</th>
                  <th className="text-left px-5 py-3 font-semibold text-gray-600">Avance</th>
                  <th className="text-left px-5 py-3 font-semibold text-gray-600">Sesiones</th>
                  <th className="px-5 py-3" />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {cajaUsers.map(u => {
                  const subStatus = u.subscription_status ?? 'trial'
                  const badge = SUB_BADGE[subStatus] ?? SUB_BADGE.trial
                  const BadgeIcon = badge.icon
                  const planLabel = u.subscription_plan ? PLANS[u.subscription_plan]?.name ?? u.subscription_plan : '—'
                  const expires = u.subscription_expires_at
                    ? new Date(u.subscription_expires_at).toLocaleDateString('es', { day: 'numeric', month: 'short', year: 'numeric' })
                    : '—'
                  return (
                    <tr key={u.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-5 py-3">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 rounded-full bg-amber-600 flex items-center justify-center text-white text-xs font-bold">
                            {u.full_name?.charAt(0) ?? '?'}
                          </div>
                          <div>
                            <div className="font-medium text-gray-900">{u.full_name}</div>
                            <div className="text-xs text-gray-400">{u.email}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-5 py-3">
                        <div className="font-medium text-gray-900">{u.project?.client_company ?? '—'}</div>
                        <div className="text-xs text-gray-400">{u.project?.industry ?? ''}</div>
                      </td>
                      <td className="px-5 py-3 text-gray-600">{planLabel}</td>
                      <td className="px-5 py-3">
                        <span className={`badge inline-flex items-center gap-1 ${badge.className}`}>
                          <BadgeIcon size={12} /> {badge.label}
                        </span>
                      </td>
                      <td className="px-5 py-3 text-gray-500">{expires}</td>
                      <td className="px-5 py-3">
                        <div className="flex items-center gap-2">
                          <div className="w-20 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                            <div className="h-full bg-brand-600" style={{ width: `${u.project?.progress_pct ?? 0}%` }} />
                          </div>
                          <span className="text-xs text-gray-500 tabular-nums w-9">{u.project?.progress_pct ?? 0}%</span>
                        </div>
                      </td>
                      <td className="px-5 py-3 text-gray-600 tabular-nums">{u.session_credits_remaining ?? 0}</td>
                      <td className="px-5 py-3">
                        <div className="flex justify-end gap-1">
                          {u.project?.consultancy_id && (
                            <>
                              <button
                                onClick={() => onExport(u.project.consultancy_id, 'full')}
                                disabled={exportingFor !== null}
                                className="flex items-center gap-1 text-xs px-2.5 py-1 rounded-lg border border-brand-200 text-brand-700 hover:bg-brand-50 disabled:opacity-60 transition-colors"
                                title="Descargar plan completo (Word)"
                              >
                                <FileText size={12} /> {exportingFor === `${u.project.consultancy_id}:full` ? '…' : 'Plan'}
                              </button>
                              <button
                                onClick={() => onExport(u.project.consultancy_id, 'executive')}
                                disabled={exportingFor !== null}
                                className="flex items-center gap-1 text-xs px-2.5 py-1 rounded-lg border border-gray-200 text-gray-700 hover:bg-gray-50 disabled:opacity-60 transition-colors"
                                title="Descargar resumen ejecutivo (Word)"
                              >
                                <FileText size={12} /> {exportingFor === `${u.project.consultancy_id}:executive` ? '…' : 'Resumen'}
                              </button>
                            </>
                          )}
                          <button onClick={() => deleteUser(u)} className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors" title="Eliminar usuario">
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </>
  )
}
